import { SocialLoginProviders } from './types';

/**
 * The provider values the exchange endpoint accepts.
 *
 * The request names the provider. The token cannot supply it: not all providers
 * issue an `iss` claim, and the Google flow sends no token.
 *
 * `google_oauth2` makes the exchange redeem an authorization code at Google.
 * `google` makes it verify an ID token. The wrong value fails a good credential.
 *
 * @see https://consumer-api.floyt.com/auth/openapi
 */
export const EXCHANGE_PROVIDERS = {
  [SocialLoginProviders.APPLE]: 'apple',
  [SocialLoginProviders.GOOGLE]: 'google_oauth2',
} as const;

/** Apple sends these on the first authorization only. */
export const APPLE_SCOPE = 'name email';

/**
 * The Return URL path registered on the Apple Service ID.
 *
 * No request goes to this path, and it needs no route. In popup mode Apple
 * sends the result as a DOM event.
 *
 * The path points into the funnel. The landing pages use the same origin, and
 * Apple only compares the string, so the funnel registration also covers them.
 */
export const APPLE_REDIRECT_PATH = '/reservation/account/social-login/apple';

/**
 * The locale in the Return URL. This is the default locale of the whitelabel,
 * not the locale of the visitor. It keeps one registered URL for each origin.
 *
 * The German origin serves `en`, so `en` sends `de-de`.
 */
export const APPLE_REDIRECT_LOCALES: Record<string, string> = {
  'de-DE': 'de-de',
  'de-CH': 'de-ch',
  'fr-FR': 'fr-fr',
  en: 'de-de',
};

/**
 * Errors `AppleID.auth.signIn()` rejects with.
 *
 * @see https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js
 */
export const APPLE_ERRORS = {
  POPUP_CLOSED: 'popup_closed_by_user',
  POPUP_BLOCKED: 'popup_blocked_by_browser',
  NEW_FLOW_TRIGGERED: 'user_trigger_new_signin_flow',
} as const;

/**
 * The scopes the code client requests.
 *
 * `openid` makes the code redeemable for an ID token. `email` puts the address
 * into that token, which the exchange matches to an account. `profile` gives
 * the backend a name for a new account.
 */
export const GOOGLE_SCOPE = 'openid email profile';

/**
 * The codes the code client reports. A window that answers sends
 * `CodeResponse.error`. A window that does not open sends the `type` that
 * `error_callback` receives.
 *
 * @see https://developers.google.com/identity/oauth2/web/reference/js-reference
 */
export const GOOGLE_ERRORS = {
  POPUP_CLOSED: 'popup_closed',
  POPUP_BLOCKED: 'popup_failed_to_open',
  CONSENT_DENIED: 'access_denied',
} as const;

/**
 * The provider codes that mean the user stopped. These report no error, and the
 * user can make the choice again.
 *
 * One set holds both providers. No two codes use the same spelling.
 */
export const CANCELLED_ERRORS: ReadonlySet<string> = new Set([
  APPLE_ERRORS.POPUP_CLOSED,
  APPLE_ERRORS.NEW_FLOW_TRIGGERED,
  GOOGLE_ERRORS.POPUP_CLOSED,
  GOOGLE_ERRORS.CONSENT_DENIED,
]);

/** The provider codes that mean the browser blocked the provider window. */
export const BLOCKED_ERRORS: ReadonlySet<string> = new Set([
  APPLE_ERRORS.POPUP_BLOCKED,
  GOOGLE_ERRORS.POPUP_BLOCKED,
]);

/**
 * `type` values the exchange answers with.
 *
 * @see https://consumer-api.floyt.com/auth/openapi
 */
export const EXCHANGE_ERROR_TYPES = {
  UNVERIFIED_EMAIL: '/auth/errors/login/unverified-email',
  INVALID_CREDENTIALS: '/auth/errors/login/invalid-credentials',
} as const;

/** Status codes the exchange answers with where it names no type of its own. */
export const STATUS_FORBIDDEN = 403;
export const STATUS_PRECONDITION_REQUIRED = 428;

/** `error_code` values for failures that no provider and no contract names. */
export const NONCE_UNAVAILABLE = 'nonceUnavailable';
export const INCOMPLETE_SESSION = 'incompleteSession';
/** The tokens arrived, but the server did not create a session from them. */
export const SESSION_NOT_ESTABLISHED = 'sessionNotEstablished';
/** The browser could not get the provider SDK. */
export const SDK_LOAD_FAILED = 'sdkLoadFailed';
/** The SDK loaded, but it put no interface on the window. */
export const SDK_UNAVAILABLE = 'sdkUnavailable';

/** The `error_code` for an exchange failure that carries no `type`. */
export const exchangeStatusCode = (status: number) => `exchange${status}`;
